import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spin, Result, Button } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { BASE_URL } from "../../../utils/constants";

const ThreeDSecureCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("");
  const [transactionId, setTransactionId] = useState("");

  const hasProcessedRef = useRef(false);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (isProcessingRef.current || hasProcessedRef.current) {
      return;
    }

    const getTransactionId = () => {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('id');
    };

    const currentTransactionId = getTransactionId();

    if (!currentTransactionId) {
      setStatus("error");
      setMessage("Error: No se encontró información de transacción");
      setTimeout(() => navigate("/marketplace/shopping-cart", { replace: true }), 3000);
      return;
    }

    setTransactionId(currentTransactionId);
    isProcessingRef.current = true;

    const processPayment = async () => {
      try {
        const verifyUrl = `${BASE_URL}/cart/3d-secure-response?id=${currentTransactionId}`;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        const response = await fetch(verifyUrl, { signal: controller.signal });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Error HTTP ${response.status}`);
        }

        const result = await response.json();

        hasProcessedRef.current = true;
        isProcessingRef.current = false;

        if (result.success) {
          setStatus("success");
          setMessage(result.message || "Pago completado exitosamente");

          localStorage.removeItem('pending_cart_id');
          localStorage.removeItem('pending_cart_total');

          setTimeout(() => {
            navigate("/marketplace/shopping-cart", {
              replace: true,
              state: {
                paymentCompleted: true,
                transactionId: currentTransactionId,
                message: result.message,
                orderId: result.order_id
              }
            });
          }, 2000);
        } else {
          setStatus("error");
          setMessage(result.message || "El pago fue rechazado");

          setTimeout(() => {
            navigate("/marketplace/shopping-cart", {
              replace: true,
              state: {
                paymentFailed: true,
                transactionId: currentTransactionId,
                error: result.message
              }
            });
          }, 3000);
        }

      } catch (error) {
        isProcessingRef.current = false;

        setStatus("error");
        setMessage(error.name === 'AbortError'
          ? "Tiempo de espera agotado. El servidor no respondió."
          : `Error: ${error.message || "No se pudo verificar el pago"}`
        );

        setTimeout(() => {
          navigate("/marketplace/shopping-cart", {
            replace: true,
            state: {
              paymentFailed: true,
              error: error.message
            }
          });
        }, 3000);
      }
    };

    processPayment();

    return () => { };
  }, [navigate]);

  useEffect(() => {
    if (status !== "processing") return;

    const safetyTimer = setTimeout(() => {
      if (status === "processing") {
        setStatus("error");
        setMessage("El proceso tardó demasiado. Por favor intenta nuevamente.");

        setTimeout(() => {
          navigate("/marketplace/shopping-cart", { replace: true });
        }, 2000);
      }
    }, 30000);

    return () => clearTimeout(safetyTimer);
  }, [status, navigate]);

  const renderContent = () => {
    switch (status) {
      case "processing":
        return (
          <Result
            icon={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            title="Verificando tu pago"
            subTitle={
              <div>
                <p>Por favor, espera un momento...</p>
                {transactionId && (
                  <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                    Transacción: {transactionId}
                  </p>
                )}
              </div>
            }
          />
        );

      case "success":
        return (
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />}
            title="Pago Exitoso"
            subTitle={message}
            extra={[
              <Button
                type="primary"
                key="continue"
                onClick={() => navigate("/marketplace/shopping-cart", { replace: true })}
              >
                Ver mi carrito
              </Button>
            ]}
          />
        );

      case "error":
        return (
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ fontSize: 48, color: "#ff4d4f" }} />}
            title="Error en el pago"
            subTitle={message}
            extra={[
              <Button
                type="primary"
                key="retry"
                onClick={() => navigate("/marketplace/shopping-cart", { replace: true })}
              >
                Volver al carrito
              </Button>
            ]}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '600px', width: '100%' }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default React.memo(ThreeDSecureCallback);